import { discUnits, lineAmount, lineDisc, type MasterItem, settingCsv, settingNum } from '@sj/shared';
import { fmtDay, fmtDT, num, rp } from './format.js';
import { esc } from './html.js';
import { label } from './status.js';

// Dokumen cetak (nota / surat jalan / SPO) — port docHtml() v1 (ops.js). Dicetak lewat printHtml() (lib/export.ts).
// Judul, label kolom & tanda tangan dari Pengaturan → Label dokumen cetak; rekening dari Data Master → Rekening transfer.

/** Bagian /api/meta yang dibutuhkan dokumen cetak. */
export interface DocMeta {
  settings: Record<string, string>;
  master: MasterItem[];
}

/** Rekening transfer aktif, dipisah " / ". */
export const banksTxt = (m: DocMeta): string =>
  m.master
    .filter((x) => x.kind === 'bank')
    .map((x) => x.name)
    .join(' / ');

/** Baris kolom tanda tangan dari daftar label (Pengaturan, dipisah koma). */
export const signRow = (labels: string[], cls = 'sign'): string =>
  `<div class="${cls}">${labels.map((l) => `<section><p>${esc(l)}</p><div></div></section>`).join('')}</div>`;

export interface DocHead {
  ship_date?: string;
  date?: string;
  spo_no?: string;
  store_name: string;
  store_address?: string;
  store_phone?: string;
  sales_name?: string;
  driver_name?: string | null;
  pay_method?: string;
  pay_plan?: string;
  due_date?: string | null;
  signed_at?: string | null;
  signed_name?: string;
  sign_file?: string | null;
  sign_hash?: string;
}

export interface DocItem {
  name: string;
  unit: string;
  qty: number;
  qty_received?: number | null;
  price: number;
  disc?: number;
}

export function docHtml(
  meta: DocMeta,
  title: string,
  no: string,
  n: DocHead,
  items: DocItem[],
  qtyKey: 'qty' | 'qty_received' = 'qty',
): string {
  const s = meta.settings;
  const bank = banksTxt(meta);
  let total = 0;
  const anyDisc = items.some((i) => (i.disc ?? 0) > 0);
  const rows = items
    .map((i, k) => {
      const q = (i[qtyKey] as number | null | undefined) ?? i.qty;
      const amt = lineAmount(q, i.price, i.disc);
      total += amt;
      return `<tr><td>${k + 1}</td><td>${esc(i.name)}</td><td class="r">${num(q)} ${esc(i.unit)}</td><td class="r">${rp(i.price)}</td>${anyDisc ? `<td class="r">${i.disc ? `-${rp(i.disc)}` : ''}</td>` : ''}<td class="r">${rp(amt)}</td></tr>`;
    })
    .join('');
  return `<div class="doc"><div class="head"><div><h1>${esc(s.business_name)}</h1><div>${esc(s.address)}</div><div>${esc(s.phone)}</div></div>
      <div class="r"><h1>${esc(title)}</h1><div>No: <b>${esc(no)}</b></div><div>Tanggal: ${fmtDay(n.ship_date || n.date)}</div>${n.spo_no ? `<div>SPO: ${esc(n.spo_no)}</div>` : ''}</div></div>
      <div><b>Kepada:</b> ${esc(n.store_name)}<br>${esc(n.store_address || '')}${n.store_phone ? `<br>Telp: ${esc(n.store_phone)}` : ''}</div>
      <div>Sales: ${esc(n.sales_name || '')}${n.driver_name ? ` · Driver: ${esc(n.driver_name)}` : ''} · Pembayaran: <b>${label('pm', n.pay_method || n.pay_plan)}</b>${n.due_date && n.pay_method === 'tempo' ? ` (jatuh tempo ${fmtDay(n.due_date)})` : ''}</div>
      <table><thead><tr><th>No</th><th>Barang</th><th class="r">Qty</th><th class="r">Harga</th>${anyDisc ? '<th class="r">Diskon/sat</th>' : ''}<th class="r">Jumlah</th></tr></thead><tbody>${rows}</tbody>
      <tfoot><tr><th colspan="${anyDisc ? 5 : 4}" class="r">TOTAL TAGIHAN</th><th class="r">${rp(total)}</th></tr></tfoot></table>
      ${bank ? `<div>Transfer ke: <b>${esc(bank)}</b></div>` : ''}
      ${n.signed_at ? `<div class="doc-sign">${n.sign_file ? `<img src="/api/files/${esc(n.sign_file)}" alt="">` : ''}<div><b>Disetujui toko secara digital</b><br>${esc(n.signed_name)} · ${fmtDT(n.signed_at)}${n.sign_hash ? `<br>Kode verifikasi: <b>${esc(n.sign_hash)}</b>` : ''}</div></div>` : ''}
      ${signRow(settingCsv(s, 'doc_sign_spo'))}
      <div class="foot">${esc(s.nota_footer)}</div></div>`;
}

// ---------------------------------------------------------------------------------------------------------------
// Nota Penjualan (tempo/transfer) / Nota Kontan (cash) — tata letak formulir owner: kop usaha + salesman, toko,
// kolom Pcs · Quantity · Harga Satuan · Disc Khusus · Rincian · Jumlah, dikelompokkan per divisi (Total 1, Total 2 …),
// "Yang harus dibayar", catatan per baris, TTD Hormat Kami (Administrasi) / Pembeli (Pemilik Toko) & inisial admin.

export interface NotaHead extends DocHead {
  no: string;
  area?: string;
  sales_phone?: string;
  admin_name?: string;
  admin_initials?: string;
}
export interface NotaItem extends DocItem {
  division?: string;
  kemasan?: string;
}

const uc = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

/** Ukuran kertas nota: 1/3 F4 (sedikit baris) atau 1/2 F4 — Pengaturan → Cetak nota. */
export function notaPaper(settings: Record<string, string>, rows: number): 'p3' | 'p2' {
  const mode = settings.nota_paper || 'auto';
  if (mode === 'f4_3') return 'p3';
  if (mode === 'f4_2') return 'p2';
  return rows <= settingNum(settings, 'nota_third_max_items') ? 'p3' : 'p2';
}

export function notaDocHtml(meta: DocMeta, n: NotaHead, items: NotaItem[], qtyKey: 'qty' | 'qty_received' = 'qty'): string {
  const s = meta.settings;
  const bank = banksTxt(meta);
  const pm = n.pay_method || n.pay_plan || '';
  const title = pm === 'cash' ? s.doc_title_cash : s.doc_title_tempo;
  // Kelompok per divisi (urutan kemunculan); barang tanpa divisi satu kelompok sendiri.
  const groups = new Map<string, NotaItem[]>();
  for (const i of items) {
    const k = i.division || '';
    groups.set(k, [...(groups.get(k) ?? []), i]);
  }
  const many = groups.size > 1;
  let grand = 0;
  let gi = 0;
  let no = 0;
  const body = [...groups.entries()]
    .map(([div, list]) => {
      gi += 1;
      let sub = 0;
      let qty = 0;
      const units = new Set(list.map((i) => i.unit.toLowerCase()));
      const rows = list
        .map((i) => {
          no += 1;
          const q = (i[qtyKey] as number | null | undefined) ?? i.qty;
          const dk = lineDisc(q, i.disc ?? 0);
          const amt = lineAmount(q, i.price, i.disc ?? 0);
          sub += amt;
          qty += q;
          const rincian = i.disc ? `${discUnits(q)} ${uc(i.unit)} x @${rp(i.disc)}` : i.kemasan || '';
          return `<tr><td class="c">${no}</td><td>${esc(i.name)}</td><td class="c">${esc(uc(i.unit))}</td><td class="r">${num(q)}</td><td class="r">${rp(i.price)}</td><td class="r">${dk ? rp(dk) : '0'}</td><td class="rinci">${esc(rincian)}</td><td class="r">${rp(amt)}</td></tr>`;
        })
        .join('');
      grand += sub;
      const unitLbl = units.size === 1 ? uc(list[0]?.unit ?? '') : '';
      const head = many ? `<tr class="grp"><td colspan="8">${esc(div || 'Lainnya')}</td></tr>` : '';
      const foot = `<tr class="sub"><td colspan="2" class="r">Sub Total${many ? ` · Total ${gi}` : ''}</td><td class="c">${esc(unitLbl)}</td><td class="r">${num(qty)}</td><td colspan="3"></td><td class="r">${rp(sub)}</td></tr>`;
      return head + rows + foot;
    })
    .join('');
  const totLbl = many
    ? `Yang harus dibayar (${[...groups.keys()].map((_, k) => `Total ${k + 1}`).join(' + ')})`
    : 'Yang harus dibayar';
  const notes = (s.nota_footer || '')
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  const size = notaPaper(s, items.length + (many ? groups.size * 2 : 1));
  const kv = (k: string, v: string) => `<tr><td>${k}</td><td>${v}</td></tr>`;
  return `<div class="doc nota-doc nota-${size}">
    <div class="nota-top">
      <div class="nota-co">
        <b class="nota-name">${esc((s.business_name || '').toUpperCase())}</b>
        ${[s.address, s.address2]
          .filter(Boolean)
          .map((a) => `<div>${esc(a)}</div>`)
          .join('')}
        ${s.phone ? `<div>${esc(s.doc_office_label || '')} : ${esc(s.phone)}${s.phone_note ? ` (${esc(s.phone_note)})` : ''}</div>` : ''}
        <div class="nota-sales">Salesman : <b>${esc(n.sales_name || '')}</b>${n.sales_phone ? ` · ${esc(n.sales_phone)}` : ''}</div>
      </div>
      <div class="nota-title"><b>${esc(title || '')}</b><span>${esc(n.no)}</span></div>
      <table class="nota-kv"><tbody>
        ${kv('Tanggal', fmtDay(n.ship_date || n.date))}
        ${kv('Toko', `<b>${esc(n.store_name)}</b>`)}
        ${kv('Alamat', `${esc(n.store_address || '')}${n.area ? ` · ${esc(n.area)}` : ''}`)}
        ${kv('Tilpun', esc(n.store_phone || ''))}
        ${pm === 'tempo' ? kv('Jatuh Tempo', `<b>${n.due_date ? fmtDay(n.due_date) : ''}</b>`) : ''}
        ${n.spo_no ? kv('SPO', esc(n.spo_no)) : ''}
      </tbody></table>
    </div>
    <table class="nota-t"><thead><tr><th class="c">No</th><th>${esc(s.doc_col_product || '')}</th><th class="c">Pcs</th><th class="r">Quantity</th><th class="r">Harga Satuan</th><th class="r">Disc Khusus</th><th>Rincian</th><th class="r">Jumlah (Rp)</th></tr></thead>
      <tbody>${body}</tbody></table>
    <div class="nota-bottom">
      <div class="nota-notes">
        ${bank && pm !== 'cash' ? `<div>Transfer ke: <b>${esc(bank)}</b></div>` : ''}
        ${notes.length ? `<div class="foot">Ket :${notes.map((l) => `<div>${esc(l)}</div>`).join('')}</div>` : ''}
      </div>
      <div class="nota-total"><span>${esc(totLbl)}</span><b>${rp(grand)}</b></div>
    </div>
    <div class="nota-sign">
      <section><p>${esc(s.doc_sign_seller || '')}</p><div class="box">${n.admin_initials ? `<em>${esc(n.admin_initials)}</em>` : ''}</div><p class="b">${esc(s.doc_sign_seller_sub || '')}</p></section>
      <section><p>${esc(s.doc_sign_buyer || '')}</p><div class="box">${n.signed_at && n.sign_file ? `<img src="/api/files/${esc(n.sign_file)}" alt="">` : ''}</div><p class="b">${esc(s.doc_sign_buyer_sub || '')}${n.signed_at ? ` · ${esc(n.signed_name)}` : ''}</p></section>
    </div>
    ${n.signed_at ? `<div class="nota-verif">Disetujui toko secara digital ${fmtDT(n.signed_at)}${n.sign_hash ? ` · kode verifikasi <b>${esc(n.sign_hash)}</b>` : ''}</div>` : ''}
  </div>`;
}
